<template lang="html">
<div class="af-generator">
    <div v-if="loading" class="af-loading">Loading...</div>

    <template v-else>
        <div class="af-header">
            <h1>AppsFlyer Link Generator</h1>
            <p>PIDs with mapped verbals are shown first. PIDs without verbals are under See more.</p>
        </div>

        <div class="af-card">
            <div class="af-grid-2">
                <div>
                    <label class="af-label">Link type</label>
                    <div class="af-seg-group">
                        <button type="button" class="af-seg" :class="{ active: linkType === 'cta' }" @click="linkType = 'cta'">CTA only</button>
                        <button type="button" class="af-seg" :class="{ active: linkType === 'cta_vta' }" @click="linkType = 'cta_vta'">CTA + VTA</button>
                    </div>
                </div>
                <div>
                    <label class="af-label">OS</label>
                    <div class="af-seg-group">
                        <button type="button" class="af-seg" :class="{ active: os === 'ios' }" @click="os = 'ios'">iOS</button>
                        <button type="button" class="af-seg" :class="{ active: os === 'android' }" @click="os = 'android'">Android</button>
                    </div>
                </div>
            </div>

            <div class="af-field">
                <label class="af-label">Ad network / AF account — pid (multi-select)</label>
                <div class="af-pills">
                    <button
                        v-for="n in networks"
                        :key="n.pid"
                        type="button"
                        class="af-pill"
                        :class="{ active: selected.includes(n.pid) }"
                        :title="`${n.pid} — codes: ${n.codes.join(', ')}`"
                        @click="toggleNet(n.pid)"
                    >
                        {{ n.name }} <span class="af-pill-pid">({{ n.pid }})</span>
                    </button>
                </div>

                <template v-if="showMore">
                    <div class="af-subsection-label">PIDs without verbals — shared reserve codes</div>
                    <div class="af-pills">
                        <button
                            v-for="n in otherNetworks"
                            :key="n.pid"
                            type="button"
                            class="af-pill af-pill-other"
                            :class="{ active: selected.includes(n.pid) }"
                            :title="`${n.pid} — reserve pool: ${n.codes.join(', ')}`"
                            @click="toggleNet(n.pid)"
                        >
                            {{ n.name }} <span class="af-pill-pid">({{ n.pid }})</span>
                        </button>
                    </div>
                </template>

                <button type="button" class="af-toggle-more" @click="showMore = !showMore">
                    {{ showMore ? 'Show fewer' : `See more (${otherNetworks.length} other networks)` }}
                </button>
                <p class="af-hint">{{ selected.length }} selected. Each account sets its own pid and draws af_adset / af_ad from its codes (adset ≠ ad).</p>
            </div>

            <div class="af-field">
                <label class="af-label">Bundle ID</label>
                <input
                    v-model="bundleId"
                    type="text"
                    class="af-input"
                    :class="{ 'af-input-error': bundleError }"
                    :placeholder="os === 'ios' ? '284882215 (numeric App Store ID)' : 'com.company.app'"
                />
                <p v-if="bundleError" class="af-error">{{ bundleError }}</p>
                <p class="af-hint">
                    {{ os === 'ios' ? 'URL → app.appsflyer.com/id&lt;your id&gt;' : 'URL → app.appsflyer.com/&lt;package&gt; (no "id" prefix)' }}
                </p>
            </div>

            <div class="af-field">
                <label class="af-label">Agency — af_prt (optional)</label>
                <input
                    v-model="agency"
                    list="agency-list"
                    type="text"
                    class="af-input"
                    placeholder="Pick from list or type your own"
                />
                <datalist id="agency-list">
                    <option v-for="a in agencies" :key="a.pid" :value="a.pid">{{ a.name }}</option>
                </datalist>
            </div>

            <div class="af-grid-3">
                <div>
                    <label class="af-label">Campaign name — c</label>
                    <input v-model="campaignName" type="text" class="af-input" placeholder="summer_promo" />
                </div>
                <div>
                    <label class="af-label">Cost model — af_cost_model</label>
                    <div class="af-seg-group">
                        <button type="button" class="af-seg" :class="{ active: costModel === 'cpi' }" @click="costModel = 'cpi'">CPI</button>
                        <button type="button" class="af-seg" :class="{ active: costModel === 'cpa' }" @click="costModel = 'cpa'">CPA</button>
                    </div>
                </div>
                <div>
                    <label class="af-label">Is retargeting?</label>
                    <div class="af-seg-group">
                        <button type="button" class="af-seg" :class="{ active: isRetargeting === 'no' }" @click="isRetargeting = 'no'">No</button>
                        <button type="button" class="af-seg" :class="{ active: isRetargeting === 'yes' }" @click="isRetargeting = 'yes'">Yes</button>
                    </div>
                </div>
            </div>

            <div class="af-field">
                <label class="af-label">Additional parameters (optional)</label>
                <input
                    v-model="additionalParams"
                    type="text"
                    class="af-input af-input-mono"
                    placeholder="af_keywords=foo&custom_x=bar"
                />
                <p class="af-hint">Appended raw to the end. Leading "&amp;" or "?" is stripped.</p>
            </div>

            <div v-if="isRetargeting === 'yes'" class="af-grid-2 af-retarget-windows">
                <div class="af-field">
                    <label class="af-label">af_reengagement_window</label>
                    <input
                        v-model="reengagementWindow"
                        type="text"
                        class="af-input af-input-mono"
                        placeholder="30d"
                    />
                </div>
                <div class="af-field">
                    <label class="af-label">af_inactivity_window</label>
                    <input
                        v-model="inactivityWindow"
                        type="text"
                        class="af-input af-input-mono"
                        placeholder="10d"
                    />
                </div>
            </div>

            <div class="af-advanced">
                <div class="af-advanced-header">
                    <button type="button" class="af-advanced-toggle" @click="advancedOpen = !advancedOpen">
                        {{ advancedOpen ? '▾' : '▸' }} Advanced — edit any parameter
                    </button>
                    <button
                        v-if="advancedOpen"
                        type="button"
                        class="af-advanced-reset"
                        @click="resetAdvanced"
                    >
                        Reset
                    </button>
                </div>
                <div v-show="advancedOpen" class="af-advanced-body">
                    <p class="af-hint">Clear a field to omit it from the URL. Blank af_channel keeps random-per-link.</p>
                    <div class="af-advanced-grid">
                        <div v-for="field in advancedFields" :key="field.key" class="af-field">
                            <label class="af-label">{{ field.label }}</label>
                            <input
                                v-model="advanced[field.key]"
                                type="text"
                                class="af-input af-input-mono"
                                :placeholder="field.placeholder"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <p v-if="error" class="af-error af-error-block">{{ error }}</p>

            <button type="button" class="af-generate-btn" @click="generate">Generate links</button>
        </div>

        <div v-if="results" class="af-results">
            <div class="af-results-header">
                <h2>Generated — {{ results.length }} account{{ results.length > 1 ? 's' : '' }}</h2>
                <div class="af-results-actions">
                    <button type="button" class="af-copy-btn" @click="generate">Re-roll</button>
                    <button type="button" class="af-copy-btn" @click="copyText(allText, 'all')">
                        {{ copiedKey === 'all' ? 'Copied ✓' : 'Copy all' }}
                    </button>
                </div>
            </div>

            <div v-for="r in results" :key="r.pid" class="af-result-card">
                <div class="af-result-meta">
                    <span class="af-result-name">{{ r.name }}</span>
                    <span class="af-badge">pid={{ r.pid }}</span>
                    <span class="af-badge">af_channel={{ r.channel }}</span>
                    <span class="af-badge">adset={{ r.adset }}</span>
                    <span class="af-badge">ad={{ r.ad }}</span>
                </div>

                <div class="af-link-block">
                    <div class="af-link-label">CTA</div>
                    <div class="af-link-row">
                        <code class="af-link-code">{{ r.cta }}</code>
                        <button type="button" class="af-copy-btn" @click="copyText(r.cta, r.pid + '-cta')">
                            {{ copiedKey === r.pid + '-cta' ? 'Copied ✓' : 'Copy' }}
                        </button>
                    </div>
                </div>

                <div v-if="r.vta" class="af-link-block">
                    <div class="af-link-label">VTA</div>
                    <div class="af-link-row">
                        <code class="af-link-code">{{ r.vta }}</code>
                        <button type="button" class="af-copy-btn" @click="copyText(r.vta, r.pid + '-vta')">
                            {{ copiedKey === r.pid + '-vta' ? 'Copied ✓' : 'Copy' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </template>
</div>
</template>

<script>
import {
    buildUrl,
    validateBundle,
    pickChannel,
    buildAdsetAd,
    DEFAULT_ADVANCED,
    DEFAULT_RETARGETING,
} from './appsflyer-link-utils'

const ADVANCED_FIELDS = [
    { key: 'af_click_lookback', label: 'af_click_lookback', placeholder: '7d' },
    { key: 'af_viewthrough_lookback', label: 'af_viewthrough_lookback', placeholder: '24h' },
    { key: 'af_sub_siteid', label: 'af_sub_siteid', placeholder: '{sub6}' },
    { key: 'af_ad_type', label: 'af_ad_type', placeholder: 'banner' },
    { key: 'af_media_type', label: 'af_media_type', placeholder: 'app' },
    { key: 'af_model', label: 'af_model', placeholder: '{device_model}' },
    { key: 'af_cost_currency', label: 'af_cost_currency', placeholder: 'usd' },
    { key: 'af_ua', label: 'af_ua', placeholder: '{device_ua}' },
    { key: 'af_ip', label: 'af_ip', placeholder: '{ip}' },
    { key: 'device_id', label: 'idfa / advertising_id', placeholder: '{sub5}' },
    { key: 'af_os_version', label: 'af_os_version', placeholder: '{os_version}' },
    { key: 'af_language', label: 'af_language', placeholder: '{language}' },
    { key: 'af_ad_format', label: 'af_ad_format', placeholder: 'image' },
    { key: 'af_channel', label: 'af_channel (blank = random)', placeholder: 'leave blank for random' },
]

export default {
    data() {
        return {
            loading: true,
            linkType: 'cta',
            os: 'ios',
            bundleId: '',
            agency: '',
            selected: [],
            showMore: false,
            campaignName: '',
            costModel: 'cpi',
            isRetargeting: 'no',
            reengagementWindow: DEFAULT_RETARGETING.af_reengagement_window,
            inactivityWindow: DEFAULT_RETARGETING.af_inactivity_window,
            additionalParams: '',
            advancedOpen: false,
            advanced: { ...DEFAULT_ADVANCED },
            advancedFields: ADVANCED_FIELDS,
            results: null,
            error: '',
            networks: [],
            otherNetworks: [],
            agencies: [],
            reserveCodes: [],
            copiedKey: null,
            copiedTimeout: null,
        }
    },
    computed: {
        bundleError() {
            return this.bundleId ? validateBundle(this.os, this.bundleId) : ''
        },
        withVTA() {
            return this.linkType === 'cta_vta'
        },
        allNetworks() {
            return [...this.networks, ...this.otherNetworks]
        },
        allText() {
            if (!this.results) return ''
            return this.results
                .flatMap((r) => [
                    `# ${r.name} (${r.pid}) | af_channel=${r.channel} adset=${r.adset} ad=${r.ad}`,
                    `CTA: ${r.cta}`,
                    ...(r.vta ? [`VTA: ${r.vta}`] : []),
                    '',
                ])
                .join('\n')
        },
    },
    async created() {
        await this.getdata()
        this.loading = false
    },
    beforeDestroy() {
        if (this.copiedTimeout) clearTimeout(this.copiedTimeout)
    },
    methods: {
        normalizeVerbalIds(verbalIds) {
            if (typeof verbalIds === 'string') {
                return verbalIds
                    .split(',')
                    .map((id) => parseInt(id.trim(), 10))
                    .filter((id) => !isNaN(id))
            }
            if (!Array.isArray(verbalIds)) {
                return []
            }
            return verbalIds
                .map((id) => parseInt(id, 10))
                .filter((id) => !isNaN(id))
        },
        async getdata() {
            try {
                const verbalsData = await this.$http.post(this.resources.Management.getDmVerbals, {})
                const pidData = await this.$http.post(this.resources.Management.getPid, {})

                const verbalRows = verbalsData.body.data || []
                const verbalCodeById = {}
                const generalCodes = []
                verbalRows.forEach((row) => {
                    verbalCodeById[Number(row.id)] = row.code
                    if (row.is_general === 1 || row.is_general === true) {
                        generalCodes.push(row.code)
                    }
                })
                this.reserveCodes = generalCodes
                if (!this.reserveCodes.length) {
                    this.notifyError('No general verbals found. PIDs without verbals will not generate links.')
                }

                const pidRows = (pidData.body.data || []).filter((obj) => obj.pid_text !== 'Other')
                const networksWithVerbals = []
                const networksWithoutVerbals = []
                pidRows.forEach((obj) => {
                    const verbalIds = this.normalizeVerbalIds(obj.verbal_ids)
                    const codes = verbalIds
                        .map((id) => verbalCodeById[id])
                        .filter(Boolean)
                    const network = {
                        name: obj.pid_text,
                        pid: obj.pid_value,
                        codes: codes.length ? codes : this.reserveCodes,
                    }
                    if (codes.length) {
                        networksWithVerbals.push(network)
                    } else {
                        networksWithoutVerbals.push(network)
                    }
                })

                this.networks = networksWithVerbals
                this.otherNetworks = networksWithoutVerbals

                const afPartData = await this.$http.post(this.resources.Management.getAfPart, {})
                this.agencies = afPartData.body.data.map((obj) => ({
                    name: obj.af_part_text,
                    pid: obj.af_part_value,
                }))
            } catch (err) {
                console.log(err)
                this.notifyError('Failed to load generator data')
            }
        },
        toggleNet(pid) {
            const idx = this.selected.indexOf(pid)
            if (idx === -1) {
                this.selected.push(pid)
            } else {
                this.selected.splice(idx, 1)
            }
        },
        resetAdvanced() {
            this.advanced = { ...DEFAULT_ADVANCED }
        },
        normalizeBundleId() {
            let id = this.bundleId.trim()
            if (this.os === 'ios' && id.startsWith('id')) {
                id = id.substr(2)
            }
            return id
        },
        hasWhitespace(value) {
            return /\s+/.test(value)
        },
        generate() {
            const bundleId = this.normalizeBundleId()
            const be = validateBundle(this.os, bundleId)
            if (be) {
                this.error = be
                return
            }
            if (this.selected.length === 0) {
                this.error = 'Select at least one ad network / account.'
                return
            }
            if (!this.campaignName.trim()) {
                this.error = 'Campaign name (c) is required.'
                return
            }
            if (this.hasWhitespace(this.campaignName)) {
                this.notifyError('Error - Campaign Name contains spaces.')
                return
            }
            if (this.hasWhitespace(this.agency)) {
                this.notifyError('Error - AF Prt contains spaces.')
                return
            }
            if (this.hasWhitespace(this.additionalParams)) {
                this.notifyError('Error - Additional Params contains spaces.')
                return
            }

            this.error = ''
            const invalidPid = this.selected.find((pid) => {
                const net = this.allNetworks.find((n) => n.pid === pid)
                return !net || !Array.isArray(net.codes) || !net.codes.length
            })
            if (invalidPid) {
                this.error = `No verbal codes available for pid ${invalidPid}.`
                return
            }

            const channelOverride = this.advanced.af_channel.trim()
            const out = this.selected.map((pid) => {
                const net = this.allNetworks.find((n) => n.pid === pid)
                const channel = channelOverride || pickChannel()
                const { adset, ad } = buildAdsetAd(net.codes)
                const common = {
                    os: this.os,
                    bundleId,
                    agency: this.agency,
                    pid,
                    channel,
                    campaignName: this.campaignName,
                    costModel: this.costModel,
                    isRetargeting: this.isRetargeting,
                    additionalParams: this.additionalParams,
                    adset,
                    ad,
                    advanced: this.advanced,
                    reengagementWindow: this.reengagementWindow,
                    inactivityWindow: this.inactivityWindow,
                }
                return {
                    name: net.name,
                    pid,
                    channel,
                    adset,
                    ad,
                    cta: buildUrl({ ...common, isVTA: false }),
                    vta: this.withVTA ? buildUrl({ ...common, isVTA: true }) : null,
                }
            })
            this.results = out
        },
        async copyText(text, key) {
            try {
                await navigator.clipboard.writeText(text)
            } catch {
                const ta = document.createElement('textarea')
                ta.value = text
                document.body.appendChild(ta)
                ta.select()
                document.execCommand('copy')
                document.body.removeChild(ta)
            }
            this.copiedKey = key
            if (this.copiedTimeout) clearTimeout(this.copiedTimeout)
            this.copiedTimeout = setTimeout(() => {
                this.copiedKey = null
            }, 1400)
        },
    },
}
</script>

<style lang="css" scoped>
.af-generator {
    min-height: 100%;
    padding: 24px;
    color: #0f172a;
    font-family: Inter, system-ui, -apple-system, sans-serif;
    box-sizing: border-box;
    text-align: left;
}

.af-loading {
    color: #475569;
    padding: 24px 0;
}

.af-header {
    margin-bottom: 18px;
}

.af-header h1 {
    font-size: 2rem;
    line-height: 1.1;
    font-weight: 800;
    margin: 0;
}

.af-header p {
    font-size: 0.95rem;
    color: rgba(15, 23, 42, 0.7);
    margin: 6px 0 0;
}

.af-card {
    border-radius: 20px;
    padding: 20px 24px;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid rgba(15, 23, 42, 0.1);
    box-shadow: 0 18px 60px rgba(15, 23, 42, 0.1);
    backdrop-filter: blur(10px);
    text-align: left;
}


.af-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin-block: 10px;
}

.af-grid-2 {
    display: grid;
    grid-template-columns: auto auto;
    gap: 20px;
    justify-content: start;
}

.af-grid-3 {
    display: grid;
    grid-template-columns: auto auto auto;
    gap: 20px;
    justify-content: start;
}

@media (max-width: 640px) {
    .af-grid-2,
    .af-grid-3 {
        grid-template-columns: 1fr;
    }
}

.af-seg-group {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-start;
}

.af-seg {
    padding: 8px 12px;
    font-size: 0.875rem;
    border-radius: 10px;
    border: 1px solid rgba(148, 163, 184, 0.65);
    background: rgba(255, 255, 255, 0.92);
    color: #334155;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.af-seg:hover {
    border-color: rgba(14, 165, 233, 0.5);
}

.af-seg.active {
    background: linear-gradient(135deg, #0ea5e9, #6366f1);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 10px 18px rgba(14, 165, 233, 0.22);
}

.af-field {
    margin-top: 0;
    text-align: left;
}

.af-pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-start;
}

.af-pill {
    padding: 6px 12px;
    font-size: 0.75rem;
    border-radius: 9999px;
    border: 1px solid rgba(148, 163, 184, 0.6);
    background: rgba(255, 255, 255, 0.92);
    color: #334155;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}

.af-pill:hover {
    border-color: rgba(14, 165, 233, 0.5);
}

.af-pill.active {
    background: linear-gradient(135deg, #0ea5e9, #6366f1);
    border-color: transparent;
    color: #fff;
    box-shadow: 0 8px 16px rgba(14, 165, 233, 0.22);
}

.af-pill-other {
    color: #475569;
}

.af-pill-pid {
    opacity: 0.6;
    font-family: ui-monospace, monospace;
}

.af-subsection-label {
    margin: 12px 0 8px;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
}

.af-toggle-more {
    display: block;
    margin-top: 8px;
    font-size: 0.75rem;
    color: #0284c7;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    text-align: left;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.af-toggle-more:hover {
    color: #0369a1;
}

.af-hint {
    font-size: 0.75rem;
    color: #64748b;
    margin: 4px 0 0;
}

.af-input {
    display: block;
    width: 100%;
    max-width: 420px;
    padding: 10px 12px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.98);
    border: 1px solid rgba(148, 163, 184, 0.55);
    color: #0f172a;
    font-size: 0.875rem;
    outline: none;
    box-sizing: border-box;
    text-align: left;
}

.af-input:focus {
    border-color: rgba(14, 165, 233, 0.85);
    box-shadow: 0 0 0 0.18rem rgba(14, 165, 233, 0.22);
}

.af-input-mono {
    font-family: ui-monospace, monospace;
}

.af-input-error {
    border-color: #dc2626;
}

.af-error {
    font-size: 0.75rem;
    color: #dc2626;
    margin: 4px 0 0;
}

.af-error-block {
    font-size: 0.875rem;
}

.af-retarget-windows {
    margin-top: 8px;
}

.af-advanced {
    margin: 16px 0 12px;
    border: 1px solid rgba(148, 163, 184, 0.45);
    border-radius: 12px;
    background: rgba(248, 250, 252, 0.7);
    overflow: hidden;
}

.af-advanced-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
}

.af-advanced-toggle {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.875rem;
    font-weight: 600;
    color: #334155;
    cursor: pointer;
    text-align: left;
}

.af-advanced-toggle:hover {
    color: #0284c7;
}

.af-advanced-reset {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.75rem;
    color: #0284c7;
    cursor: pointer;
    text-decoration: underline;
    text-underline-offset: 2px;
}

.af-advanced-reset:hover {
    color: #0369a1;
}

.af-advanced-body {
    padding: 0 14px 14px;
    border-top: 1px solid rgba(148, 163, 184, 0.35);
}

.af-advanced-body .af-hint {
    margin: 10px 0 8px;
}

.af-advanced-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px 16px;
}

@media (max-width: 640px) {
    .af-advanced-grid {
        grid-template-columns: 1fr;
    }
}

.af-advanced-grid .af-input {
    max-width: none;
}

.af-generate-btn {
    display: inline-block;
    width: auto;
    padding: 12px 24px;
    border-radius: 10px;
    background: linear-gradient(135deg, #10b981, #22c55e);
    border: none;
    color: #fff;
    font-weight: 700;
    font-size: 1rem;
    cursor: pointer;
    text-align: left;
    transition: background 0.15s;
}

.af-generate-btn:hover {
    filter: brightness(1.03);
}

.af-results {
    margin-top: 24px;
}

.af-results > * + * {
    margin-top: 16px;
}

.af-results-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 8px;
}

.af-results-header h2 {
    font-size: 0.875rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #475569;
    margin: 0;
}

.af-results-actions {
    display: flex;
    gap: 8px;
}

.af-result-card {
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(15, 23, 42, 0.1);
    border-radius: 14px;
    padding: 16px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.af-result-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 12px;
}

.af-result-name {
    font-size: 0.875rem;
    font-weight: 700;
    color: #0f172a;
}

.af-badge {
    font-size: 0.75rem;
    padding: 2px 8px;
    border-radius: 9999px;
    background: rgba(248, 250, 252, 1);
    border: 1px solid rgba(148, 163, 184, 0.5);
    color: #475569;
    font-family: ui-monospace, monospace;
}

.af-link-block + .af-link-block {
    margin-top: 12px;
}

.af-link-label {
    font-size: 0.75rem;
    color: #64748b;
    margin-bottom: 4px;
}

.af-link-row {
    display: flex;
    gap: 8px;
    align-items: flex-start;
}

.af-link-code {
    flex: 1;
    font-size: 0.75rem;
    color: #1e293b;
    background: #f8fafc;
    border: 1px solid rgba(148, 163, 184, 0.5);
    border-radius: 8px;
    padding: 10px;
    word-break: break-all;
    font-family: ui-monospace, monospace;
    line-height: 1.6;
    white-space: pre-wrap;
}

.af-seg,
.af-pill,
.af-copy-btn {
    text-align: left;
}

.af-copy-btn {
    flex-shrink: 0;
    padding: 6px 12px;
    font-size: 0.75rem;
    font-weight: 500;
    border-radius: 10px;
    background: linear-gradient(135deg, #64748b, #475569);
    border: none;
    color: #fff;
    cursor: pointer;
    transition: background 0.15s;
}

.af-copy-btn:hover {
    filter: brightness(1.04);
}
</style>
